import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useWeb3React, UnsupportedChainIdError } from '@web3-react/core';
import {
  NoBscProviderError,
  UserRejectedRequestError as UserRejectedBSCRequestError,
} from '@binance-chain/bsc-connector';
import {
  NoEthereumProviderError,
  UserRejectedRequestError as UserRejectedRequestErrorInjected,
} from '@web3-react/injected-connector';
import {
  UserRejectedRequestError as UserRejectedRequestErrorWalletConnect,
  WalletConnectConnector,
} from '@web3-react/walletconnect-connector';

// store
import { useNetworkStore } from '@/store';

// constants
import connectors, { ConnectorNames } from 'constants/connectors';

// hooks
import useEagerConnect, {
  connectorLocalStorageKey,
} from '@/hooks/useEagerConnect';
import useAuthenticateWallet from '@/hooks/useAuthenticateWallet';
import { toast } from 'react-toastify';
import useInactiveListener from '@/hooks/useInactiveListener';

type AuthProviderProps = {
  children?: ReactNode;
};

type AuthContext = {
  isLoading: boolean;
  account?: string | null;
  active?: boolean;
  login: (connectorId: ConnectorNames) => Promise<void>;
  logout: () => void;
};

const AuthContext = createContext<AuthContext>({
  isLoading: false,
  login: () => {
    throw new Error(`login has not been implemented`);
  },
  logout: () => {
    throw new Error(`logout has not been implemented`);
  },
});

function AuthProvider({ children }: AuthProviderProps) {
  const {
    activate,
    deactivate,
    account: web3Account,
    error,
    connector,
  } = useWeb3React();

  const [active, setActive] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [account, setAccount] = useState<string | null | undefined>();

  const { network, setNetwork } = useNetworkStore();
  const { connectorsByName } = connectors(network);

  const requestSecret = useAuthenticateWallet();

  // try to eagerly connect to an injected provider, if it exists and has granted access already
  const triedEager = useEagerConnect();

  // when there's no account connected, react to logins (broadly speaking) on the injected provider, if it exists
  useInactiveListener(!triedEager);

  const logout = useCallback(() => {
    window.localStorage.removeItem(connectorLocalStorageKey);
    setNetwork(`Ethereum`);
    setActive(false);
    setIsLoading(false);
    setAccount(undefined);
    deactivate();
  }, []);

  const login = useCallback(
    async (connectorId: ConnectorNames) => {
      setIsLoading(true);
      const innerConnector = connectorsByName[connectorId];
      if (innerConnector) {
        window.localStorage.setItem(connectorLocalStorageKey, connectorId);
        await activate(innerConnector);
      } else {
        // The connector config is wrong
        setActive(false);
        setAccount(null);
        setIsLoading(false);
      }
    },
    [network, web3Account],
  );

  useEffect(() => {
    setActive(false);
    const call = async () => {
      if (web3Account) {
        try {
          await requestSecret();
          setAccount(web3Account);
          setActive(true);
          setIsLoading(false);
          // eslint-disable-next-line no-shadow
        } catch (error) {
          logout();
        }
      }
    };

    call();
  }, [web3Account]);

  useEffect(() => {
    if (
      error instanceof UserRejectedRequestErrorInjected ||
      error instanceof UserRejectedRequestErrorWalletConnect ||
      error instanceof UserRejectedBSCRequestError
    ) {
      if (connector instanceof WalletConnectConnector) {
        const walletConnector = connector as WalletConnectConnector;
        walletConnector.walletConnectProvider = null;
      }
      toast.error(`Please authorize to access your account`, {
        toastId: `reject`,
      });
    } else if (
      error instanceof NoEthereumProviderError ||
      error instanceof NoBscProviderError
    ) {
      toast.error(`No provider was found`, {
        toastId: `provider`,
      });
    } else if (error instanceof UnsupportedChainIdError) {
      toast.error(`Unsupported Chain Id Error. Check your chain Id.`, {
        toastId: `chainId`,
      });
    }

    if (error) {
      setIsLoading(false);
    }
  }, [error]);

  return (
    <AuthContext.Provider value={{ account, login, logout, isLoading, active }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error(`useAuth must be used within an AuthProvider`);
  }
  return context;
}

export default AuthProvider;
